import { useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { safeReturnTo } from "@mycompbox/shared";
import { useCompleteGitHubInstall } from "@/hooks/queries/use-github";
import { GITHUB_RETURN_TO_KEY } from "@/lib/github-return-to";
import { usePageTitle } from "@/hooks/use-page-title";
import { Button } from "@/components/ui/button";

function readStoredReturnTo(): string | null {
  try {
    return safeReturnTo(sessionStorage.getItem(GITHUB_RETURN_TO_KEY));
  } catch {
    return null;
  }
}

function clearStoredReturnTo() {
  try {
    sessionStorage.removeItem(GITHUB_RETURN_TO_KEY);
  } catch {
    // Storage blocked: nothing to clear.
  }
}

/**
 * Where GitHub sends an admin back after installing the CompBox app
 * (the App's "Setup URL"). Finishes the connection, then returns to where the
 * admin started (the server-signed returnTo, else the one stored before leaving),
 * falling back to Settings.
 */
export function GitHubCallbackPage() {
  usePageTitle("Connecting GitHub");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const complete = useCompleteGitHubInstall();
  const started = useRef(false);

  const installationId = params.get("installation_id");
  const code = params.get("code");
  const state = params.get("state");

  useEffect(() => {
    if (started.current || !installationId || !code || !state) return;
    started.current = true;
    complete.mutate(
      { installationId, code, state },
      // Back into the app: onboarding continues there if anything is left.
      {
        onSuccess: (res) => {
          const target = safeReturnTo(res.data.returnTo) ?? readStoredReturnTo() ?? "/settings#github";
          clearStoredReturnTo();
          void navigate(target, { replace: true });
        },
      }
    );
  }, [installationId, code, state, complete, navigate]);

  // Someone without admin rights on the GitHub organization asked for the app:
  // a GitHub admin has to approve it first.
  if (params.get("setup_action") === "request") {
    const back = readStoredReturnTo();
    return (
      <div className="mx-auto max-w-md space-y-4 pt-16 text-center">
        <CheckCircle2 className="mx-auto h-8 w-8 text-amber-400" />
        <p className="text-sm text-muted-foreground">
          Request sent. An owner of your GitHub organization needs to approve the CompBox app. Once they have, click
          Connect GitHub {back ? "again" : "in Settings again"} to finish.
        </p>
        <div className="flex justify-center gap-2">
          {back ? (
            <Button asChild size="sm">
              <Link to={back}>{back.startsWith("/teams/") ? "Back to project" : "Go back"}</Link>
            </Button>
          ) : null}
          <Button asChild size="sm" variant={back ? "outline" : "default"}>
            <Link to="/settings#github">Back to Settings</Link>
          </Button>
        </div>
      </div>
    );
  }

  // GitHub sends no code when only the repository choice changed; nothing to finish.
  if (!installationId || !code || !state) {
    return (
      <div className="mx-auto max-w-md space-y-4 pt-16 text-center">
        <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-400" />
        <p className="text-sm text-muted-foreground">Your GitHub changes are saved.</p>
        <Button asChild size="sm">
          <Link to="/settings#github">Back to Settings</Link>
        </Button>
      </div>
    );
  }

  if (complete.isError) {
    return (
      <div className="mx-auto max-w-md space-y-4 pt-16 text-center">
        <XCircle className="mx-auto h-8 w-8 text-destructive" />
        <p className="text-sm">{complete.error instanceof Error ? complete.error.message : "Connecting GitHub failed."}</p>
        <Button asChild size="sm" variant="outline">
          <Link to="/settings#github">Back to Settings</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-md items-center justify-center gap-2 pt-16 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      Connecting GitHub…
    </div>
  );
}
